/**
 * VRG-156 — per-form capability LADDER + size gating + best_for_product_types derivation.
 *
 * Product = form factor (7 products). Each form is sold with a *ladder* of
 * capabilities (validCaps) the customer selects; each (capability, size) combo is
 * pruned by `capSizes` so impossible variants (e.g. dual-freq Trace on a small round
 * tag, Vault on tiny sizes) never exist. Mirrors supply/tag-shop-catalog.md +
 * supply/scripts/shopify-create-tag-products.mjs 1:1.
 *
 * best_for_product_types(form, cap) = (form's category affinity) ∩ (categories recommending cap).
 * NEVER hand-list a product's best_for_product_types — always derive via `bestForFor` /
 * `bestForForForm`.
 *
 * ⚠️ "CATEGORY" IN THIS FILE'S OLDEST NAMES MEANS PRODUCT TYPE. `CATEGORY_CAPABILITIES`
 * and `FORM_AFFINITY` are keyed on ALL_PRODUCT_TYPES — the 68 product TYPES — not on the 23
 * product CATEGORIES in @repo/types. The vocabulary guard at the bottom is what pins
 * that, and it is why the names were not "fixed": renaming them is a migration of its
 * own. VRG-610's `bestForCategoriesForForm` is the one thing here that returns real
 * categories, and its return type says so.
 */
import {ALL_PRODUCT_TYPES, categoriesForProductTypes, type ProductCategory} from '@repo/types'
import type {TagCapability, TagFormFactor, TagShape} from './pricing'
import {shapeOfSize} from './pricing'

// ─── Category → recommended capabilities (many-to-many) ──────────────────────
// A category may recommend more than one capability (e.g. Handbags → Verify for
// regular, Verify + Vault for grails).

export const CATEGORY_CAPABILITIES: Record<string, TagCapability[]> = {
  'Aerospace & Defense': ['Verify + Trace'],
  'Apparel & Clothing': ['Verify', 'Verify + Trace'],
  Art: ['Verify + Vault'],
  'Audio Equipment': ['Verify', 'Verify + Trace'],
  'Auto Parts': ['Verify', 'Verify + Trace'],
  Automobiles: ['Verify', 'Verify + Vault'],
  Belts: ['Verify'],
  Bicycles: ['Verify'],
  Books: ['Verify + Vault'],
  Bullion: ['Verify + Tamper'],
  Candles: ['Verify'],
  Cannabis: ['Verify + Tamper'],
  Caviar: ['Verify', 'Temperature'],
  'Cigars & Tobacco': ['Verify', 'Moisture'],
  'Coffee & Tea': ['Verify', 'Moisture'],
  Coins: ['Verify', 'Verify + Tamper'],
  Collectibles: ['Verify'],
  Comics: ['Verify', 'Verify + Tamper'],
  Cosmetics: ['Verify', 'Verify + Tamper'],
  Currency: ['Verify', 'Verify + Tamper'],
  Documents: ['Verify + Vault'],
  Credentials: ['Verify', 'Verify + Vault'],
  'Electrical Components': ['Verify + Trace'],
  Electronics: ['Verify', 'Verify + Trace'],
  'Eyewear': ['Verify'],
  Firearms: ['Verify + Trace'],
  Footwear: ['Verify', 'Verify + Trace'],
  'Fragrance & Perfume': ['Verify', 'Verify + Tamper'],
  Furniture: ['Verify'],
  'Gemstones': ['Verify', 'Verify + Tamper'],
  'Golf Equipment': ['Verify'],
  'Gourmet Food': ['Verify + Tamper', 'Temperature'],
  'Handbags': ['Verify', 'Verify + Vault', 'Verify + Trace'],
  Hazardous: ['Verify + Tamper', 'Verify + Trace'],
  'Hats': ['Verify'],
  'Home Décor': ['Verify'],
  'Industrial Machinery': ['Verify + Trace'],
  Jewelry: ['Verify', 'Verify + Vault'],
  'Lighters': ['Verify'],
  Lighting: ['Verify'],
  'Luggage': ['Verify', 'Verify + Trace'],
  'Marine & Boats': ['Verify', 'Verify + Vault'],
  'Medical Devices': ['Verify + Trace'],
  Memberships: ['Verify', 'Verify + Vault'],
  Memorabilia: ['Verify', 'Verify + Vault'],
  Motorcycles: ['Verify', 'Verify + Vault'],
  'Musical Instruments': ['Verify', 'Verify + Vault'],
  Camping: ['Verify'],
  Pharmaceuticals: ['Verify + Tamper', 'Temperature'],
  Photography: ['Verify'],
  'Rugs & Carpets': ['Verify', 'Verify + Vault'],
  'Scarves': ['Verify'],
  'Sculpture': ['Verify + Vault'],
  Skincare: ['Verify', 'Verify + Tamper'],
  Sneakers: ['Verify', 'Verify + Vault', 'Verify + Trace'],
  'Sporting Goods': ['Verify', 'Verify + Trace'],
  Stamps: ['Verify'],
  'Supplements': ['Verify + Tamper'],
  'Ticketing & Access': ['Verify', 'Verify + Tamper'],
  'Toys': ['Verify'],
  'Trading Cards': ['Verify', 'Verify + Tamper'],
  'Vinyl & Records': ['Verify'],
  'Wallets': ['Verify'],
  Watches: ['Verify', 'Verify + Vault'],
  Wine: ['Verify', 'Verify + Tamper', 'Temperature'],
  Spirits: ['Verify', 'Verify + Tamper', 'Temperature'],
  'Writing Instruments': ['Verify'],
}

// ─── Form → category affinity (physical suitability) ─────────────────────────

export const FORM_AFFINITY: Record<TagFormFactor, string[]> = {
  /* One category, because the form has one application: it mounts behind a
     watch dial. Listing anything else would advertise a fit the part does not
     physically have. */
  Dial: ['Watches'],
  /* ⚠️ 'Art' ADDED 2026-09-20 (Spencer). A certificate sticker on the back of a
     frame or a stretcher bar is a real placement for this form — flat, interior,
     hidden from view, which is exactly what the Sticker's own blurb claims.

     It is listed here rather than on the website because FORM_AFFINITY is the
     source: TAGS.md, tag-catalog.generated.json, the Shopify product build and
     the site's mirrored `best_for_product_categories` all derive from it. Adding the
     chip on the website alone would have thrown its module-load guard, which is
     how this ended up here. */
  Sticker: [
    /* ⚠️ 'Photography' ADDED 2026-09-25 (Spencer). A print's REVERSE is blank and
       flat — the image face can never take adhesive. Spencer: Art and Photography
       "have the same front side so it's fine," i.e. the placement reasoning that
       already covers Art covers this too. Photography had NO affinity entry at all
       before this, so it was one of the nine types no form served. */
    'Photography',
    'Art',
    /* ⚠️ 'Handbags' ADDED 2026-09-21 (Spencer, via the marketing site). The
       placement is the interior of a centre flap — flat, leather, hidden when
       the bag is closed — which is exactly what this form's own blurb claims:
       "applied to a flat interior surface, hidden from view".

       Same route as 'Art' above. The website's tag-form row could not offer a
       Sticker for Handbags, because its module-load guard refuses a highlight
       that is not in the catalogue's best_for_product_categories; asserting it on the
       site alone would have thrown. FORM_AFFINITY is the source — TAGS.md,
       tag-catalog.generated.json, the Shopify product build and the site's
       mirror all derive from here — so this is where it goes.

       ⚠️ THIS IS A COMMERCIAL CHANGE, NOT ONLY AN EDITORIAL ONE: it makes the
       Sticker a recommended form for Handbags in the shop. Re-run the emitter,
       then the website's `pnpm check:tag-forms`, after committing. */
    'Handbags',
    /* ⚠️ 'Wallets' ADDED 2026-09-25 (VRG-630), inherited from the removed `Hang`
       form. The placement is the leather interior of a fold — flat, hidden when
       the wallet is closed — which is the Handbags case above with a smaller
       object. A wallet has no loop to hang from and no seam to sew into, so the
       Sticker is the only form that physically fits it. */
    'Wallets',
    /* ⚠️ 'Gemstones' ADDED 2026-09-25 (Spencer, VRG-631). A loose stone cannot
       carry a tag — but it does not travel loose. It travels in a parcel paper or
       a certified box, and that box has a flat blank face, which is exactly what
       an adhesive label wants. The tag is on the package the stone is sold in.

       ⚠️ BEFORE THIS ENTRY AND ITS `Closure` SIBLING, GEMSTONES WAS SERVED BY NO
       FORM AT ALL. It is an approved industry-page candidate that resolved to
       zero forms — a type the catalogue recommended nothing for, which reads on
       the shop as "we do not sell anything for this". These two entries are what
       unblock that page.

       Gemstones recommends ['Verify', 'Verify + Tamper'] and this ladder offers
       both, so it publishes under both here, and under `Verify + Tamper` on
       Closure. */
    'Gemstones',
    /* ⚠️ 'Luggage' ADDED 2026-09-25 (Spencer, VRG-631). A case has an interior
       panel — a lining, a lid face, the flat inside of a shell — and that is the
       Handbags placement above on a larger object. It is the SECOND of the two
       surfaces a case offers: the rigid outer shell takes the `Solid` disc, the
       interior panel takes this label.

       Luggage recommends ['Verify', 'Verify + Trace'] and this ladder offers
       both, so it publishes under both. Note it is already on `FabricLabel` for
       the seam position (VRG-630) — these are three different placements on one
       object, not a duplicate. */
    'Luggage',
    'Wine', 'Spirits', 'Fragrance & Perfume', 'Cosmetics', 'Skincare', 'Candles',
    'Gourmet Food', 'Coffee & Tea', 'Documents', 'Electronics',
    'Audio Equipment', 'Trading Cards', 'Currency', 'Coins', 'Stamps', 'Comics', 'Toys',
    'Collectibles', 'Vinyl & Records', 'Books', 'Pharmaceuticals', 'Supplements',
    'Cannabis', 'Cigars & Tobacco', 'Memberships', 'Ticketing & Access', 'Credentials', 'Sporting Goods',
    'Golf Equipment', 'Caviar', 'Hazardous',
  ],
  /* ⚠️ 'Art' ADDED 2026-09-20 (Spencer), for the same reason as Sticker above
     and a different placement: a rigid disc fixes to a plinth, a frame edge or a
     mount, where an adhesive label would not hold. Both forms now project to the
     `art` CATEGORY — see PRODUCT_TYPE_CATEGORIES, where Art, Sculpture and
     Photography all map to it. */
  Solid: [
    /* ⚠️ 'Photography' ADDED 2026-09-25 (Spencer). A print's REVERSE is blank and
       flat — the image face can never take adhesive. Spencer: Art and Photography
       "have the same front side so it's fine," i.e. the placement reasoning that
       already covers Art covers this too. Photography had NO affinity entry at all
       before this, so it was one of the nine types no form served. */
    'Photography',
    'Art',
    /* ⚠️ 'Sculpture', 'Writing Instruments' and 'Memorabilia' ADDED 2026-09-25
       (VRG-630), inherited from the removed `Hang` form. All three are the
       adhesive-free half of this form's own blurb — "a two-sided disc you can
       hang, seat in a recess, or fix yourself" — and Solid's existing 'Art'
       note already authored the placement: "a plinth, a frame edge or a mount,
       where an adhesive label would not hold".

         Sculpture            the Art × Solid placement, verbatim
         Writing Instruments  a metal body, small: the ⌀6mm on-metal case
         Memorabilia          a signed ball or glove is DESTROYED by adhesive,
                              and cannot take a sewn patch either

       ⚠️ 'Memorabilia' IS HERE AGAINST THE BRIEF, WHICH ASSIGNED IT TO
       `ClosureLoop` ALONE — and that assignment does not work. best_for is
       DERIVED: Memorabilia recommends ['Verify', 'Verify + Vault'] and
       ClosureLoop offers only ['Verify + Tamper'], so the intersection is empty
       and the type would publish under no form at all. Solid intersects it at
       both. ClosureLoop keeps the type too, recording the intent; Solid is what
       makes the catalogue serve it. See VRG-630 — Spencer to confirm. */
    'Sculpture', 'Writing Instruments', 'Memorabilia',
    /* ⚠️ 'Luggage' ADDED 2026-09-25 (Spencer, VRG-631). A case has a RIGID
       SHELL — a moulded polycarbonate or aluminium exterior — and a rigid disc
       fixes to it where an adhesive label on a surface that is dragged, stacked
       and thrown would not survive. That is this form's own 'Art' note ("a
       plinth, a frame edge or a mount, where an adhesive label would not hold")
       applied to a travelling object.

       Luggage recommends ['Verify', 'Verify + Trace']; this ladder offers
       'Verify' (and 'Connect'/'Verify + Vault', which Luggage does not
       recommend), so it publishes here under 'Verify' alone. The Trace rung it
       also wants is served by its `Sticker` and `FabricLabel` entries — one
       object, three placements, different capability reach on each. */
    'Luggage',
    'Watches', 'Jewelry', 'Eyewear', 'Electronics', 'Audio Equipment',
    'Automobiles', 'Auto Parts', 'Motorcycles', 'Bicycles', 'Industrial Machinery',
    'Aerospace & Defense', 'Electrical Components', 'Firearms', 'Sporting Goods',
    'Golf Equipment', 'Musical Instruments', 'Bullion', 'Marine & Boats', 'Coins',
    'Hazardous',
  ],
  // Fabric Patch — surface-mounted, visible branding on soft goods.
  FabricPatch: [
    'Apparel & Clothing', 'Handbags', 'Footwear', 'Sneakers', 'Hats', 'Scarves', 'Belts',
    'Sporting Goods', 'Camping',
  ],
  // Fabric Label — seam-mounted and discreet, so it also covers the linens/uniform
  // product types a visible surface patch would snag or irritate in.
  FabricLabel: [
    'Apparel & Clothing', 'Handbags', 'Footwear', 'Sneakers', 'Hats', 'Scarves', 'Belts',
    'Sporting Goods', 'Camping',
    /* ⚠️ 'Luggage' ADDED 2026-09-25 (VRG-630), inherited from the removed `Hang`
       form. A case carries interior labels in exactly the position this form
       occupies on a garment, and Luggage recommends ['Verify','Verify + Trace']
       — both of which this ladder offers, which is why the type publishes here
       and not under ClosureLoop, whose lone `Verify + Tamper` does not meet it. */
    'Luggage',
  ],
  Closure: [
    'Wine', 'Spirits', 'Fragrance & Perfume', 'Cosmetics', 'Skincare', 'Candles',
    'Gourmet Food', 'Pharmaceuticals', 'Supplements', 'Cannabis', 'Coffee & Tea',
    'Caviar', 'Hazardous',
    /* ⚠️ 'Trading Cards' ADDED 2026-09-25 (Spencer, VRG-631). A graded card does
       not live as a card — it lives SEALED IN A SLAB, and the slab is the thing
       that carries the grade. So the tag seals the slab, not the card: a tag
       applied to the card face destroys the grade it was bought for, which makes
       every card-surface form wrong here and the closure the only correct one.

       The seal IS the claim. Trading Cards recommends ['Verify', 'Verify +
       Tamper'] and this ladder offers ['Verify + Tamper', 'Temperature'], so it
       publishes under 'Verify + Tamper' — the rung that says the slab has not
       been opened, which is exactly the assurance a graded card is sold on. */
    'Trading Cards',
    /* ⚠️ 'Gemstones' ADDED 2026-09-25 (Spencer, VRG-631). A loose stone cannot be
       tagged at all — there is no surface, no interior and no loop. What the
       closure wraps is the CERTIFIED BOX (GIA-style) the stone travels in, so the
       tag binds the stone to its own certification by sealing the package that
       holds both.

       ⚠️ GEMSTONES WAS PREVIOUSLY SERVED BY NO FORM AT ALL — an approved
       industry-page candidate resolving to zero forms. This entry and its
       `Sticker` sibling are what unblock that page. See the Sticker note.

       Gemstones recommends ['Verify', 'Verify + Tamper']; this ladder meets it at
       'Verify + Tamper'. */
    'Gemstones',
  ],
  /* Closure Loop (VRG-630) — the two product types the removed `Hang` form
     served that have a LOOP to clamp and cannot take adhesive.

     ⚠️ BOTH ENTRIES DERIVE TO NOTHING TODAY, AND THAT IS NOT A MISTAKE — but
     it is also not a claim. best_for = affinity ∩ (types recommending a cap in
     this ladder). This form offers `Verify + Tamper` alone; Memorabilia
     recommends ['Verify','Verify + Vault'] and Luggage ['Verify','Verify +
     Trace'], so neither intersects and neither is published under this form.
     They are listed to record the physical suitability the form exists for, so
     that a later `Verify` rung (or a CATEGORY_CAPABILITIES change) picks them
     up automatically instead of needing to be remembered.

     Each type's PUBLISHED home is elsewhere and was set at the same time —
     Memorabilia → Solid, Luggage → FabricLabel. Do not remove those on the
     strength of the entries here. */
  ClosureLoop: [
    'Memorabilia', 'Luggage',
  ],
  // Card (new) — ID-1 credential/membership cards.
  Card: [
    'Memberships', 'Ticketing & Access', 'Credentials', 'Documents',
  ],
}

// ─── Form → { validCaps ladder, sizes, per-capability size gating } ───────────
// One PRODUCT per form; capability is a selectable variant axis climbing validCaps.
// capSizes prunes the (capability × size) grid so impossible combos never exist —
// where a capability is absent from capSizes it offers all of the form's `sizes`.
// Gating rationale (VRG-156, a "reasonable first pass" per the spec, review-time
// confirmable): large-antenna caps (Trace, Vault) gate to bigger SIZES.
//
// Revised 2026-08-06 — the previous rationale was wrong in two ways it is worth naming,
// because both came from treating a merchandising choice as a physical law:
//   * "Trace needs a rectangle" — false. Sticker ships Trace at 38mm and 45mm, both
//     ROUND. The constraint is antenna FOOTPRINT, so any form offering a large enough
//     size can carry it. Fabric Patch now does, via a 45x25mm size.
//   * "Sticker Vault is 28mm-only" — that is the watch-dial TIER, not an antenna limit,
//     and it sat oddly next to Solid shipping Vault at 18mm. Sticker's larger sizes now
//     offer it too.
// ⚠️ The true minimum footprint for Trace (EM4425) and Vault (NTAG X DNA) is still
// unconfirmed — the catalog cites ~73x23mm for a dual-freq inlay yet ships Trace on a
// 38mm round. Confirm against datasheets/RFQ before treating these gates as final.

export type FormMeta = {
  /** the capability ladder this form is sold with (ordered for display). */
  validCaps: TagCapability[]
  /** every size the form offers (drives the product.sizes list + Size facet). */
  sizes: string[]
  /** per-capability allowed sizes — prunes the variant grid. Missing cap → all `sizes`. */
  capSizes: Partial<Record<TagCapability, string[]>>
}

export const FORM_META: Record<TagFormFactor, FormMeta> = {
  // Sticker — Connect·Verify → +Tamper/+Temperature/+Moisture/+Pressure; 28mm watch-dial adds +Vault.
  Sticker: {
    validCaps: [
      'Connect', 'Verify', 'Verify + Trace', 'Verify + Tamper', 'Verify + Vault',
      'Temperature', 'Moisture', 'Pressure',
    ],
    /* ⚠️ 28mm, NOT 30mm — AND THEY WERE THE SAME PART LISTED TWICE.
       Spencer, 2026-09-18: "the sticker is actually 28mm instead of 30mm."
       The list carried both, with `28mm` appended out of numeric order when it
       was the watch-dial tier, and `Verify` gated to BOTH — which is what a
       duplicate looks like. Merged onto the true dimension rather than deleted,
       so the four capabilities floored at the old `30mm` (Tamper, Temperature,
       Moisture, Pressure) keep their smallest size instead of silently having
       it raised to 38mm.

       The Dial form keeps its own separate 28mm. Same dimension, different
       part — a Dial is ferrite-bonded and mounts behind a dial. */
    /* 20x20mm sits between the 10mm round and the 25mm round by antenna area,
       so it clears both base floors. It is added to Connect and Verify ONLY —
       the gated ladders (Trace, Tamper, the sensors, Vault) are deliberately
       left alone, because putting a size into a capability's list is a claim
       that the antenna carries that chip, and nobody has confirmed it for this
       one. A size absent from a gated capSizes simply is not offered there.

       ⚠️ 20mm AND 20x20mm ARE BOTH REAL AND BOTH LISTED — a 20mm round and a
       20x20mm square are different parts with the same nominal number, so the
       token shape is what tells them apart. The round was written as `25mm`
       until 2026-09-18 (Spencer: "sticker has 20 instead of 25mm"). Do not
       "dedupe" them. */
    sizes: ['5x5mm', '10mm', '20mm', '20x20mm', '28mm', '38mm', '45mm'],
    capSizes: {
      // Antenna floors for a flat inlay: Connect (NTAG213) → 5mm SQUARE smallest;
      // Verify (NTAG424 DNA, larger coil) → 10mm ROUND smallest. On-metal add-on
      // available at every size (onMetal: true). (Solid's ferrite disc tunes smaller —
      // 6mm — a separate regime, not this floor.)
      Connect: ['5x5mm', '20mm', '20x20mm', '28mm', '38mm', '45mm'],
      Verify: ['10mm', '20mm', '20x20mm', '28mm', '38mm', '45mm'],
      // Verify + Trace = EM4425 dual-freq — the UHF dipole needs a large antenna, so
      // only the bigger sticker diameters carry it.
      'Verify + Trace': ['38mm', '45mm'],
      'Verify + Tamper': ['28mm', '38mm', '45mm'],
      Temperature: ['28mm', '38mm', '45mm'],
      Moisture: ['28mm', '38mm', '45mm'],
      Pressure: ['28mm', '38mm', '45mm'],
      /* ⚠️ THIS COMMENT USED TO READ "28mm watch-dial only — the sole Sticker
         size with Vault", which contradicted its own line: the array has three
         sizes, and the header above records why it was widened ("that is the
         watch-dial TIER, not an antenna limit ... Sticker's larger sizes now
         offer it too"). The stale half was read back as fact on 2026-09-18.

         The watch dial is now its own form (`Dial`). Sticker KEEPS 28mm — a
         28mm sticker that is not a dial is still sold — and keeps Vault at all
         three sizes. Whether a non-dial 28mm sticker should still offer Vault
         is a merchandising question nobody has answered; it is left as-is
         rather than silently narrowed. */
      'Verify + Vault': ['28mm', '38mm', '45mm'],
    },
  },
  /* Dial Tag — the watch-dial part, split out of Sticker on 2026-09-18.
     One size, and ferrite is not optional (see `forcedOn` in pricing.ts).
     Capabilities are the set 28mm Sticker already carried. */
  Dial: {
    validCaps: ['Verify', 'Verify + Vault'],
    sizes: ['28mm'],
    capSizes: {},
  },
  // Solid Tag — Connect·Verify → +Vault (ferrite on-metal disc). No Trace/Tamper.
  Solid: {
    validCaps: ['Connect', 'Verify', 'Verify + Vault'],
    sizes: ['6mm', '12mm', '18mm', '30mm'],
    capSizes: {
      'Verify + Vault': ['18mm', '30mm'], // X DNA needs a larger antenna than the touch/small sizes
    },
  },
  // Fabric Patch — Connect·Verify. Small round/square, sewn flat on all edges.
  // NO Trace: dual-freq needs a 45x25mm rectangular dipole, which is a label shape,
  // not a patch. That size requirement is what revealed these were two products.
  FabricPatch: {
    // Trace + Vault added 2026-08-06. The earlier exclusion claimed a dual-freq dipole
    // "cannot fit a round coil" -- but Sticker already ships Trace at 38mm and 45mm,
    // both round, so the real constraint is FOOTPRINT, not shape. A patch is only
    // small because we listed small sizes; a 45x25mm sew-on patch is an ordinary thing
    // to make. Adding the large size is what unlocks both capabilities, via capSizes.
    validCaps: ['Connect', 'Verify', 'Verify + Trace', 'Verify + Vault'],
    // Square patch sizes (20x20mm smallest, 30x30mm standard) added 2026-08-11 alongside the
    // round 16/25mm. Extra-Durable grade is a ~3.5mm PPS patch (Durability add-on). Trace/Vault
    // stay gated to 45x25mm — the small squares don't clear the dual-freq / X DNA footprint.
    /* ⚠️ 25x25mm, NOT 25mm. Spencer, 2026-09-18. A patch is sewn down on all
       edges and is square — which 20x20mm and 30x30mm already said, while
       `25mm` was written as a diameter. It is the same part under a name that
       misdescribed its shape, so it is renamed rather than replaced.

       This also fixes what `shapeOfSize` reported: a one-dimension token reads
       as Round, so the catalogue was advertising a round patch that is not made.

       ⚠️ THERE IS NO 20x20mm PATCH. Spencer, 2026-09-18: "fabricpatch has
       25x25 instead of 20x20." `20x20mm` and `25mm` were the same square part
       written two ways, so both collapse onto 25x25mm rather than one being
       deleted — the same duplicate-under-two-names shape as Sticker's
       30mm/28mm.

       16mm -> 16x16mm for the same reason (Spencer, 2026-09-18), which makes
       every Fabric Patch token two-dimension and the form Rectangle-only. It
       had been reporting Round to the shop, advertising a round patch that is
       not made. */
    sizes: ['16x16mm', '25x25mm', '30x30mm', '45x25mm'],
    capSizes: {
      Connect: ['16x16mm', '25x25mm', '30x30mm', '45x25mm'],
      Verify: ['16x16mm', '25x25mm', '30x30mm', '45x25mm'],
      'Verify + Trace': ['45x25mm'],
      'Verify + Vault': ['45x25mm'],
    },
  },
  // Fabric Label — Connect·Verify·Verify+Trace. Rectangular, folded, sewn at the seam;
  // the only fabric form whose shape can carry the dual-freq dipole.
  FabricLabel: {
    validCaps: ['Connect', 'Verify', 'Verify + Trace', 'Verify + Vault'],
    sizes: ['45x25mm', '70x20mm'],
    capSizes: {
      Connect: ['45x25mm', '70x20mm'],
      Verify: ['45x25mm', '70x20mm'],
      'Verify + Trace': ['45x25mm', '70x20mm'],
      // Both label sizes clear the 45x25mm Vault footprint, so no size gate.
      'Verify + Vault': ['45x25mm', '70x20mm'],
    },
  },
  /* Closure Loop (VRG-630) — `Verify + Tamper` ONLY, and the absence of Connect
     is the whole argument for the form's existence.

     The removed `Hang` form carried `Connect` and `Verify + Tamper`. Both rungs
     were removed rather than moved here:

       Connect            a Connect-only hang tag is a PRINTED CARD WITH A
                          STICKER ON IT. Checkpoint Systems: "an RFID inlay
                          sandwiched between two pieces of card"; SML attaches
                          its inlays to hangtags "as a sticker". Selling it as
                          a distinct form sold the card, which we do not make.
                          A buyer who wants this buys a Sticker.
       Verify + Tamper    Hang claimed it on the argument that its loop IS the
                          attachment. NO SUCH PART IS MANUFACTURED: every
                          loop-form NFC product ships a non-tamper chip, and
                          every tamper-chip product is a flat disc with a tail.

     This form is the part that would make the second claim true. NXP caps a
     tamper loop at 2.5cm² of enclosed area — a conductive RING violates that by
     construction, which is why nobody sells the ring. Two adjacent conductors
     run down a 150mm cord at ≤1.67mm separation enclose 2.5cm² and satisfy it.
     That is the design; it is feasible and it is not yet made.

     ⚠️ NOT ORDERABLE. `COMING_SOON_FORMS` in capabilities.ts. Listed because a
     visible unbuyable form is demand signal, which is the same call already
     made for `Temperature` on Closure below.

     ⚠️ `LOOP` IS NOT A DIMENSION AND MUST NOT BE READ AS ONE. The cord length
     and body diameter are unspecced; nobody has measured this part. It follows
     Closure's SINGLE/DOUBLE precedent exactly — a token naming the TOPOLOGY,
     because there is exactly one topology today. A second cord length gets its
     own token, not a redefinition of this one. A real dimension invented here
     would be a fabricated specification on a product that does not exist. */
  ClosureLoop: {
    validCaps: ['Verify + Tamper'],
    sizes: ['LOOP'],
    capSizes: {},
  },
  // Closure — Verify → +Tamper or +Temperature (no Connect; descriptive size labels intentional).
  Closure: {
    // A closure is a tamper-evident neck seal / cap — it is NEVER bare Verify; the tamper
    // loop is the whole point. Ladder starts at Verify + Tamper. Temperature is a
    // frontier single-surface combo (today it's really two tags) — already coming-soon
    // globally (COMING_SOON_CAPABILITIES), so it shows but can't be ordered.
    validCaps: ['Verify + Tamper', 'Temperature'],
    /* ⚠️ ARM COUNT, NOT APPLICATION. These were `neck-band` and `cap-disc`,
       which named where the part goes — and named it too narrowly. FORM_AFFINITY
       for Closure already lists Pharmaceuticals, Supplements, Cannabis, Caviar,
       Hazardous, Cosmetics, Skincare and Candles, so the catalogue's own data
       contradicted the token: a closure seals a prescription tin, a caviar tin
       and a gemstone case, not just a bottle neck.

       Every other size token in this file describes the PART (30mm, 45x25mm,
       85.6x54mm). Closure's were the only two describing a use, which is exactly
       why they over-specified. Spencer, 2026-09-18.

       ⚠️ THESE DO NOT ENCODE A DIMENSION, and must not be read as if they do.
       Arm count is topology: a single-arm band for a prescription neck and one
       for a caviar tin are different circumferences. Two tokens cover the space
       only because there are exactly two parts today. A second single-arm
       circumference needs its own token — `SINGLE60` — not a redefinition of
       this one. */
    sizes: ['SINGLE', 'DOUBLE'],
    capSizes: {
      'Verify + Tamper': ['SINGLE', 'DOUBLE'],
      Temperature: ['SINGLE'],
    },
  },
  // Card (new) — Verify → +Vault (no Connect). ID-1 credential/membership card.
  Card: {
    validCaps: ['Verify', 'Verify + Vault'],
    sizes: ['85.6x54mm'],
    capSizes: {},
  },
}

/** Allowed sizes for a (form, capability) combo — the pruned grid. */
export function sizesFor(form: TagFormFactor, cap: TagCapability): string[] {
  return FORM_META[form].capSizes[cap] ?? FORM_META[form].sizes
}

/**
 * The shapes a form is actually orderable in, derived from its size tokens.
 *
 * ⚠️ DERIVED, NOT DECLARED — see `shapeOfSize`. The hand-written
 * `FORM_INFO[form].shapes` this replaces had Solid and the since-removed Hang
 * advertising `Rectangle` with no rectangular size to sell, which is a filter
 * that returns nothing and a promise the catalog cannot keep.
 *
 * Order is stable (Round, Rectangle, Other) rather than first-seen, so the
 * metafield value does not churn when a size is added in the middle of a list.
 */
export function shapesFor(form: TagFormFactor): TagShape[] {
  const present = new Set(FORM_META[form].sizes.map(shapeOfSize))
  return (['Round', 'Rectangle', 'Other'] as const).filter((s) => present.has(s))
}

/**
 * best_for_product_types(form, cap) = form's category affinity ∩ categories recommending cap.
 * Auto-derived — never hand-list a product's best_for_product_types.
 */
export function bestForFor(form: TagFormFactor, cap: TagCapability): string[] {
  const affinity = FORM_AFFINITY[form] ?? []
  return affinity.filter(category => (CATEGORY_CAPABILITIES[category] ?? []).includes(cap))
}

/** Union of best_for_product_types across the whole form ladder (product = form). Dedup, affinity order. */
export function bestForForForm(form: TagFormFactor): string[] {
  const caps = FORM_META[form]?.validCaps ?? []
  const matched = new Set<string>()
  for (const cap of caps) for (const c of bestForFor(form, cap)) matched.add(c)
  // Preserve the form's authored affinity order.
  return (FORM_AFFINITY[form] ?? []).filter(c => matched.has(c))
}

/**
 * best_for_product_types for a SPECIFIC selected capability combo on the product page (VRG-156):
 * (form's category affinity) ∩ (categories that recommend the selected capability).
 * Drives the live "Best for" list as the capability selection changes. Alias of
 * `bestForFor`, named for the product-page combo-selection call site.
 */
export function bestForForCombo(form: TagFormFactor, capability: TagCapability): string[] {
  return bestForFor(form, capability)
}

/**
 * VRG-610 — the FORM-LEVEL best_for_product_types at PRODUCT CATEGORY grain, for the shop grid.
 *
 * ── ⚠️ THIS IS A TRUTH FIX, NOT A LENGTH FIX ─────────────────────────────
 * `bestForForForm` is a UNION across the form's whole ladder and therefore across
 * every SIZE it offers. A union over-claims at product-type grain: "Sticker Tag is
 * best for Watches" is only true at 28mm, and the grid does not say "at 28mm".
 *
 * Resolved up to category, the same union is largely HONEST — "Sticker Tag is best
 * for Apparel" holds at 10mm and at 45mm alike — because a category is coarse enough
 * that the claim survives the narrowing every size imposes. That is why display moves
 * here. The list also gets shorter, which is a consequence and not the reason: a
 * shorter list that is still false would be no improvement.
 *
 * The three grains, per TAGS.md §5 "Best-for, settled":
 *
 *     Shop grid, form level    -> PRODUCT CATEGORY   (this function)
 *     Product page, size set   -> PRODUCT TYPE       (`bestForForCombo`)
 *     Search                   -> type resolves up   (`categoriesForProductTypes`)
 *
 * ⚠️ `best_for_product_types` AT PRODUCT-TYPE GRAIN IS NOT REPLACED. It stays on the catalog
 * product and remains the search index — "Scarves" must still find Fabric Patch, and
 * it can only do that if the fine list survives. This is a SECOND projection.
 *
 * ⚠️ `other` IS DROPPED HERE AND NOWHERE ELSE. ~20 product types have no category
 * and resolve to `other` so the map can stay total (see PRODUCT_TYPE_CATEGORIES).
 * "Best for: … , Other" tells a buyer nothing, so the DISPLAY projection omits it
 * while search keeps it. Display must be meaningful; resolution must be total.
 */
export function bestForCategoriesForForm(form: TagFormFactor): ProductCategory[] {
  return categoriesForProductTypes(bestForForForm(form), {includeOther: false})
}


/* ═══════════════════════════════════════════════════════════════════════════
   ⚠️ VOCABULARY GUARD — every category name here must be a real product type.

   `best_for_product_types` is DERIVED from the two tables above, and it is published: it
   becomes the `vera.best_for` Shopify metafield, it drives the shop's category
   filter, and /tags renders it. So a name here that is not in ALL_PRODUCT_TYPES is
   not a cosmetic mismatch — it is a category the shop offers and cannot filter
   to, and an icon map keyed on the canonical list silently renders nothing.

   ── ⚠️ THE SHORT NAMES HERE ARE CORRECT. DO NOT "FIX" THEM ───────────────
   Handbags, Eyewear, Scarves, Hats, Toys, Supplements, Sporting Goods, Auto
   Parts, Gemstones, Lighters, Luggage, Sculpture, Wallets — these are VRG-152's
   deliberate direction, not stale leftovers. That ticket renamed the taxonomy
   TOWARD brevity (Fine Art → Art, Fine Jewelry → Jewelry, Collectibles — Other
   → Collectibles) under an explicit rule:

       "Keep multi-word only where the shorter form is ambiguous."

   Eyewear already encompasses sunglasses; Handbags already encompasses purses.

   ⚠️ THIS WAS GOT WRONG ONCE, ON 2026-09-18, AND THE SYMPTOM LOOKED LIKE THE
   OPPOSITE PROBLEM. ALL_PRODUCT_TYPES in @repo/types had kept the long forms, so a
   mismatch appeared between the two lists — and the tempting reading is "this
   file never migrated". The direction of the fix was backwards: these names
   were right and the canonical list was the one that had not followed VRG-152
   through. Fifteen names were renamed the wrong way and then reverted.

   VRG-152's own closing note lists what a rename must update, and the product types
   list is first on it. It is the file that missed the cascade.

   `Wine` and `Spirits` are separate here, where this file previously had one
   `Wine & Spirits` entry. That is not a reversal of the above — it is the
   granularity split doing its job: a product type is the specific thing, and
   wine and spirits are separately taggable.

   ⚠️ THIS IS NOT A TEST, it is a module-load throw, because the failure is
   silent in both directions: a bad name emits a category nothing matches, and
   a bad name in only one table emits nothing while looking correct in source.
   ═══════════════════════════════════════════════════════════════════════════ */
{
  const canonical = new Set<string>(ALL_PRODUCT_TYPES)
  const used = new Set<string>([
    ...Object.keys(CATEGORY_CAPABILITIES),
    ...Object.values(FORM_AFFINITY).flat(),
  ])
  const unknown = [...used].filter((v) => !canonical.has(v)).sort()
  if (unknown.length) {
    throw new Error(
      `affinity.ts uses ${unknown.length} category name(s) that are not product ` +
        `types in @repo/types ALL_PRODUCT_TYPES: ${unknown.join(', ')}. ` +
        `best_for_product_types is derived from these tables and is published to Shopify, so a ` +
        `name that is not canonical becomes a category the shop cannot filter to.`,
    )
  }
}
